import React, { useState } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles, fade } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/SearchRounded';
import InputBase from '@material-ui/core/InputBase';
import IconButton from '@material-ui/core/IconButton';
import AccountCircle from '@material-ui/icons/AccountCircle';
import Popover from '@material-ui/core/Popover';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Switch from '@material-ui/core/Switch';
import Avatar from '@material-ui/core/Avatar';
import PlaceIcon from '@material-ui/icons/PlaceRounded';
import PersonIcon from '@material-ui/icons/PersonRounded';
import DeleteIcon from '@material-ui/icons/DeleteRounded';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Fuse from 'fuse.js';
import { ClickAwayListener } from '@material-ui/core';
import Link from '@material-ui/core/Link';

import * as actions from '../../../store/actions';

const useStyles = makeStyles(theme => ({
  documentation: {
    color: theme.palette.common.white,
    textDecoration: 'none',
    margin: 8
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: fade(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(3),
      width: 'auto',
    },
  },
  searchIcon: {
    width: theme.spacing(7),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  grow: {
    flexGrow: 1,
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 7),
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 180,
      '&:focus': {
        width: 300,
      },
    },
  },
  searchResults: {
    width: 400
  }
}));

const propertyOptions = {
  shouldSort: true,
  includeScore: true,
  threshold: 0.32,
  location: 0,
  distance: 50,
  maxPatternLength: 32,
  minMatchCharLength: 3,
  keys: ['displayName', 'place.formattedAddress'],
  id: 'propertyId'
};

const userOptions = {
  shouldSort: true,
  includeScore: true,
  threshold: 0.2,
  location: 0,
  distance: 50,
  maxPatternLength: 32,
  minMatchCharLength: 3,
  keys: ['displayName', 'userId'],
  id: 'userId'
};

let propertyIndex;
let userIndex;

const KirsAdminAppBar = ({ properties, users, divisions, appUser, history, dispatch }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [query, setQuery] = useState('');
  const [propertyResults, setPropertyResults] = useState([]);
  const [userResults, setUserResults] = useState([]);

  const searchOpen = Boolean(searchAnchorEl) && query !== '';
  const searchId = searchOpen ? 'search-popover' : undefined;
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const createSearchIndices = () => {
    if (Object.keys(properties).length > 0) {
    propertyIndex = new Fuse(Object.values(properties), propertyOptions);
    }
    if (Object.keys(users).length > 0) {
    userIndex = new Fuse(Object.values(users), userOptions);
    }
  };

  // Initialize search indices when component mounts or data changes
  React.useEffect(() => {
    createSearchIndices();
  }, [properties, users]);

  const renderResults = () => {
    const results = [];

    const combinedResults = [...propertyResults.slice(0,10), ...userResults.slice(0,10)]
    combinedResults.sort((x,y) => x.score - y.score);
    for(const result of combinedResults.slice(0,10)) {
      // Check if this is a property result
      if(result.item.propertyId && properties[result.item.propertyId]) {
        if (!includeDeleted && properties[result.item.propertyId].isDeleted) continue;
        results.push( 
          <ListItem 
            onClick={
              () => {
                setQuery('');
                setSearchAnchorEl(null);
                history.push(`/map/properties/${result.item.propertyId}`)
              }
            } 
            button 
            key={result.item.propertyId}
          >
            <ListItemAvatar>
              <Avatar>
                {
                  properties[result.item.propertyId].isDeleted ? (
                    <DeleteIcon />
                  ) : (
                    <PlaceIcon />
                  )
                }
              </Avatar>
            </ListItemAvatar>
            <ListItemText 
              secondary={properties[result.item.propertyId].place.formattedAddress} 
              primary={properties[result.item.propertyId].displayName}
            />
          </ListItem>
        )
      } 
      // Check if this is a user result
      else if (result.item.userId && users[result.item.userId]) {
        if (!includeDeleted && users[result.item.userId].isDeleted) continue;
        results.push( 
          <ListItem 
            onClick={
              () => {
                setQuery('');
                setSearchAnchorEl(null);
                history.push(`/map/users/${result.item.userId}`);
              } 
            } 
            button 
            key={result.item.userId}
          >
            <ListItemAvatar>
              <Avatar>
                {
                  users[result.item.userId].isDeleted ? (
                    <DeleteIcon />
                  ) : (
                    <PersonIcon />
                  )
                }
              </Avatar>
            </ListItemAvatar>
            <ListItemText 
              secondary={users[result.item.userId].userId} 
              primary={users[result.item.userId].displayName}
            />
          </ListItem>
        )
      }
    }

    return results;
  };

  return (
    <AppBar position='fixed' className={classes.appBar}>
      <Toolbar>
        <Typography variant='h6' noWrap>
          Kirs Hub
        </Typography>
        <div className={classes.grow} />
        <div className={classes.search}>
          <div className={classes.searchIcon}>
            <SearchIcon />
          </div>
          <InputBase
            placeholder='Search…'
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
            }}
            value={query}
            inputProps={{ 'aria-label': 'search' }}
            onChange={
              e => {
                const value = e.target.value;
                setQuery(value)
                
                // Ensure search indices exist before searching
                if (propertyIndex && value.length < 15 && value.length > 2) {
                  try {
                    setPropertyResults(propertyIndex.search(value));
                  } catch (error) {
                    console.warn('Property search error:', error);
                    setPropertyResults([]);
                  }
                } else if (value.length <= 2) {
                  setPropertyResults([]);
                }
                
                if (userIndex && value.length < 15 && value.length > 2) {
                  try {
                    setUserResults(userIndex.search(value));
                  } catch (error) {
                    console.warn('User search error:', error);
                    setUserResults([]);
                  }
                } else if (value.length <= 2) {
                  setUserResults([]);
                }
                
                if(value.length > 2) {
                  setSearchAnchorEl(e.currentTarget);
                } else {
                  setSearchAnchorEl(null);
                } 
              }
            }
            onFocus={createSearchIndices}
          />
        </div>
        <a
          href='https://kirs.zendesk.com/hc/en-us'
          className={classes.documentation}
        >
          Documentation
        </a>
        <IconButton
          edge='end'
          onClick={e => setAnchorEl(e.currentTarget)}
          color='inherit'
        >
          <AccountCircle />
        </IconButton>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}

        >
          <Button
            onClick={
              () => {
                setAnchorEl(null)
                dispatch(actions.auth.signOut());
              }
            }
          >
            Log Out
          </Button>
        </Popover>
        <Popper
          id={searchId}
          open={searchOpen}
          disablePortal={true}
          anchorEl={searchAnchorEl}
          placement='bottom-start'
        >
          <ClickAwayListener onClickAway={() => setSearchAnchorEl(null)} >
            <Paper className={classes.searchResults}>
              <List>
              <ListItem>
                <ListItemIcon>
                  <DeleteIcon />
                </ListItemIcon>
                <ListItemText primary='Include Archived' />
                <ListItemSecondaryAction>
                  <Switch
                    edge='end'
                    onChange={() => setIncludeDeleted(!includeDeleted)}
                    checked={includeDeleted}
                  />
                </ListItemSecondaryAction>
              </ListItem>
                { renderResults() }
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Toolbar>
    </AppBar>
  )
};

moment.fn.zoneName = function() {
  const abbr = this.zoneAbbr();
  return abbrs[abbr] || abbr;
};

const abbrs = {
  EST : 'Eastern Standard Time',
  EDT : 'Eastern Daylight Time',
  CST : 'Central Standard Time',
  CDT : 'Central Daylight Time',
  MST : 'Mountain Standard Time',
  MDT : 'Mountain Daylight Time',
  PST : 'Pacific Standard Time',
  PDT : 'Pacific Daylight Time',
};

const mapStateToProps = state => ({
  properties: state.properties.byId,
  users: state.users.byId,
  divisions: state.divisions.byId,
  appUser: state.users.byId[state.auth.user.username]
});

export default withRouter(connect(mapStateToProps)(KirsAdminAppBar));